import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { logService } from '../../services/logService';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import {
    Search,
    Filter,
    Calendar,
    UserCheck,
    AlertTriangle
} from 'lucide-react';

const LogsPage = () => {
    const { user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalLogs, setTotalLogs] = useState(0);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10
    });
    const [filters, setFilters] = useState({
        start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        user_id: user.user_type === 'admin' ? '' : user.user_id,
        log_type: '',
        severity: ''
    });

    useEffect(() => {
        fetchLogs();
    }, [pagination.currentPage, filters]);

    const fetchLogs = async () => {
        setIsLoading(true);
        try {
            const { data, meta } = await logService.getLogs({
                page: pagination.currentPage,
                limit: pagination.perPage,
                ...filters
            });

            setLogs(data);
            setTotalLogs(meta.total);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            user_id: user.user_type === 'admin' ? '' : user.user_id,
            log_type: '',
            severity: ''
        });
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const getSeverityBadge = (severity) => {
        const severityStyles = {
            'info': 'bg-blue-100 text-blue-800',
            'warning': 'bg-yellow-100 text-yellow-800',
            'error': 'bg-red-100 text-red-800',
            'critical': 'bg-red-500 text-white'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${severityStyles[severity] || 'bg-gray-100 text-gray-800'}`}>
                {
                    {
                        'info': '提示',
                        'warning': '警告',
                        'error': '錯誤',
                        'critical': '嚴重'
                    }[severity] || severity
                }
            </span>
        );
    };

    const columns = [
        {
            header: '操作時間',
            accessor: 'created_at',
            cell: (row) => (
                <span className="text-sm text-gray-600">
                    {new Date(row.created_at).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    })}
                </span>
            )
        },
        {
            header: '用戶',
            accessor: 'user_name',
            cell: (row) => (
                <div className="flex items-center">
                    <UserCheck className="w-5 h-5 mr-2 text-gray-500" />
                    <span>{row.user_name}</span>
                </div>
            )
        },
        {
            header: '日誌類型',
            accessor: 'log_type',
            cell: (row) => (
                <span className="text-sm text-gray-700">{row.log_type}</span>
            )
        },
        {
            header: '嚴重程度',
            accessor: 'severity',
            cell: (row) => getSeverityBadge(row.severity)
        },
        {
            header: '描述',
            accessor: 'description',
            cell: (row) => (
                <span className="text-sm text-gray-600 truncate max-w-xs block">
                    {row.description}
                </span>
            )
        },
        {
            header: 'IP地址',
            accessor: 'ip_address',
            cell: (row) => (
                <span className="text-sm text-gray-500">{row.ip_address}</span>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">操作日誌</h1>
            </div>

            {/* 篩選器 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                    <Calendar className="w-5 h-5 text-gray-500 mr-2" />
                    <h3 className="font-bold">篩選條件</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            開始日期
                        </label>
                        <input
                            type="date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleDateChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            結束日期
                        </label>
                        <input
                            type="date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleDateChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            日誌類型
                        </label>
                        <select
                            name="log_type"
                            value={filters.log_type}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="">所有類型</option>
                            <option value="login">登入</option>
                            <option value="logout">登出</option>
                            <option value="create">新增</option>
                            <option value="update">更新</option>
                            <option value="delete">刪除</option>
                            <option value="system">系統</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            嚴重程度
                        </label>
                        <select
                            name="severity"
                            value={filters.severity}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="">所有級別</option>
                            <option value="info">提示</option>
                            <option value="warning">警告</option>
                            <option value="error">錯誤</option>
                            <option value="critical">嚴重</option>
                        </select>
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleResetFilters}
                        className="flex items-center text-sm text-blue-500 hover:text-blue-700"
                    >
                        <Filter className="w-4 h-4 mr-1" />
                        重置篩選
                    </button>
                </div>
            </div>

            {/* 日誌列表 */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <DataTable
                            columns={columns}
                            data={logs}
                            emptyMessage="無操作日誌"
                        />
                        <div className="p-4 border-t border-gray-200">
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalItems={totalLogs}
                                perPage={pagination.perPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LogsPage;